import { useRef, useEffect, useState } from 'react';
import { RefreshCw } from "lucide-react";
import { useAppDispatch, useAppSelector, type RootState } from '@/store';
import { fetchChatList, fetchMessageList } from '@/mainApi';
import { setChatId, setMessages, doneChatListReload } from '@/messages/messageSlice';
import { setChatList } from '@/chats/chatSlice';
import ChatItem from './ChatItem';

export default function ChatList() {
  const dispatch = useAppDispatch();

  const userId = useAppSelector((s: RootState) => s.user.userId);
  const sidebarOpen = useAppSelector((s: RootState) => s.chats.sidebarOpen);
  const reloadChatlist = useAppSelector((s: RootState) => s.messages.reloadChatlist);
  const chatList = useAppSelector((s: RootState) => s.chats.chatList);
  const chatId = useAppSelector((s: RootState) => s.messages.chatId);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevSidebarOpen = useRef(false);

  useEffect(() => {
    if (!sidebarOpen || !userId) return;

    const justOpened = sidebarOpen && !prevSidebarOpen.current;
    prevSidebarOpen.current = sidebarOpen;

    const shouldFetch = justOpened || reloadChatlist;
    if (!shouldFetch) return;

    let mounted = true;
    setLoading(true);
    setError(null);

    fetchChatList(userId)
      .then((list) => mounted && dispatch(setChatList(list)))
      .catch((err) => {
        if (!mounted) return;
        setError(err?.message ?? 'Failed');
        dispatch(setChatList([]));
      })
      .finally(() => {
        if (mounted && reloadChatlist) {
          dispatch(doneChatListReload());
        }
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [sidebarOpen, reloadChatlist, userId, dispatch]);

  async function manualReload() {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const list = await fetchChatList(userId);
      dispatch(setChatList(list));
    } catch (err: any) {
      setError(err?.message ?? "Failed to reload chats");
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectChat(chatId: string) {
    dispatch(setChatId(chatId));

    const msgs = await fetchMessageList(userId, chatId);
    dispatch(setMessages(msgs));

    if (window.innerWidth < 768) {
      dispatch({ type: 'chats/closeSidebar' });
    }
  }

  if (loading) {
    return (
      <div className="p-3 text-sm text-slate-700 flex flex-col items-center gap-2">
        Loading chats…
        <button
          onClick={() => manualReload()}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-400"
        >
          <RefreshCw size={14} className="animate-spin-slow" />
          Refresh
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 text-sm text-red-400 flex flex-col items-center gap-2">
        {error}
        <button
          onClick={() => manualReload()}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-400 text-slate-700 text-xs"
        >
          <RefreshCw size={14} />
          Try Again
        </button>
      </div>
    );
  }

  if (!chatList.length) return <div className="px-3 py-2 text-sm text-slate-400">No chats yet</div>;

  return (
    <div className="flex flex-col gap-1 p-3 overflow-y-auto">
      {chatList.map((c) => (
        <ChatItem key={c.id} id={c.id} title={c.title} isActive={c.id === chatId} onSelect={handleSelectChat} />
      ))}
      {chatList.length === 0 && (
        <div className="text-sm text-slate-400 text-center py-4"> No conversations yet.</div>
      )}
    </div>
  );
}
